import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { alpha } from "@mui/material/styles";
import type { FormEvent } from "react";
import type { TenantDiscoveryResponse } from "@/features/auth/model/tenant-discovery";
import { SsoButtons } from "@/features/auth/ui/login-form/sso-buttons";
import type { LoadingState } from "@/features/auth/ui/login-form/use-login-flow";
import { Callout } from "@/shared/ui/callout";

interface OrganisationChipProps {
  name: string;
}

function OrganisationChip({ name }: OrganisationChipProps) {
  return (
    <Chip
      label={`Organisation: ${name}`}
      variant="outlined"
      sx={(theme) => ({
        alignSelf: "flex-start",
        bgcolor: alpha(theme.palette.primary.main, 0.12),
        borderColor: alpha(theme.palette.primary.main, 0.32),
        color: theme.palette.text.secondary,
      })}
    />
  );
}

interface PasswordStepProps {
  email: string;
  password: string;
  error: string | null;
  loading: LoadingState;
  loadingSsoProviderId: string | null;
  tenantInfo: TenantDiscoveryResponse | null;
  onPasswordChange: (password: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onSsoSignIn: (providerId: string) => void;
  onChangeEmail: () => void;
}

export function PasswordStep({
  email,
  password,
  error,
  loading,
  loadingSsoProviderId,
  tenantInfo,
  onPasswordChange,
  onSubmit,
  onSsoSignIn,
  onChangeEmail,
}: PasswordStepProps) {
  const hasSso = (tenantInfo?.providers.length ?? 0) > 0;
  const hasPassword = tenantInfo?.tenant.email_password_enabled ?? false;

  return (
    <Stack spacing={2.5}>
      {error && <Callout severity="error">{error}</Callout>}

      {tenantInfo && <OrganisationChip name={tenantInfo.tenant.display_name} />}

      <Stack component="form" onSubmit={onSubmit} spacing={2}>
        <TextField
          id="email-display"
          label="Email"
          type="email"
          value={email}
          slotProps={{
            input: {
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    type="button"
                    variant="text"
                    size="small"
                    onClick={onChangeEmail}
                    disabled={loading !== null}
                    aria-label="Change email address"
                    sx={{ minHeight: 0, p: 0, color: "text.secondary" }}
                  >
                    Change
                  </Button>
                </InputAdornment>
              ),
            },
          }}
          autoComplete="email"
          fullWidth
        />

        {hasPassword && (
          <>
            <TextField
              id="password"
              label="Password"
              type="password"
              value={password}
              onChange={(event) => onPasswordChange(event.target.value)}
              required
              disabled={loading !== null}
              autoComplete="current-password"
              fullWidth
            />
            <Button type="submit" variant="contained" disabled={loading !== null} fullWidth>
              {loading === "password" ? "Signing in..." : "Sign In"}
            </Button>
          </>
        )}
      </Stack>

      {hasSso && hasPassword && (
        <Divider sx={{ color: "text.secondary", fontSize: 11, letterSpacing: "0.14em" }}>
          OR SIGN IN WITH
        </Divider>
      )}

      {hasSso && tenantInfo && (
        <SsoButtons
          providers={tenantInfo.providers}
          disabled={loading !== null}
          loadingProviderId={loadingSsoProviderId}
          onSsoSignIn={onSsoSignIn}
        />
      )}
    </Stack>
  );
}
